import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import SubHeader from "components/common/SubHeader";
import "assets/scss/pages/withdraw.scss";
import { withdrawUser } from "api/user";
import { TokenManager, notifyNativeLogout } from "utils/api";
import useUserStore from "@store/useUserStore";

const WITHDRAW_REASONS = [
    { id: "rarely_use", label: "서비스를 잘 사용하지 않아요" },
    { id: "inconvenient", label: "이용이 불편해요" },
    { id: "other_service", label: "다른 업체의 서비스를 이용할 예정이에요" },
    { id: "expensive", label: "서비스 이용 요금이 비싸요" },
    { id: "other", label: "기타" },
];

const Withdraw = () => {
    const navigate = useNavigate();
    const clearUserInfo = useUserStore((state) => state.clearUserInfo);
    const [selectedReason, setSelectedReason] = useState("");
    const [isAgreed, setIsAgreed] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleReasonChange = useCallback((reasonId) => {
        setSelectedReason(reasonId);
    }, []);

    const handleCheckboxChange = useCallback((e) => {
        setIsAgreed(e.target.checked);
    }, []);

    const handleKeyDown = useCallback((e) => {
        if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setIsAgreed((prev) => !prev);
        }
    }, []);

    const handleWithdraw = useCallback(async () => {
        if (!isAgreed || isSubmitting) return;

        setIsSubmitting(true);

        try {
            if (selectedReason === "") {
                alert("탈퇴 사유를 선택해주세요.");
                return;
            }
            withdrawUser().then(async (res) => {
                notifyNativeLogout();
                await TokenManager.clearTokens();
                localStorage.removeItem("userInfoStorage");
                clearUserInfo();
                alert("회원탈퇴가 완료되었습니다.");
                window.location.href = "/";
            });
        } catch (error) {
            console.error("회원탈퇴 실패:", error);
            alert("회원탈퇴 처리 중 오류가 발생했습니다. 다시 시도해주세요.");
        } finally {
            setIsSubmitting(false);
        }
    }, [isAgreed, isSubmitting, navigate]);

    return (
        <div className="page-withdraw">
            <SubHeader title="회원탈퇴" />

            <div className="withdraw-content">
                <section className="withdraw-section">
                    <p className="section-title">
                        (주) 버킷리스트 클래스 서비스를 아껴주신 시간에
                        감사드립니다:) <br />
                        서비스를 사용하며 느끼신 불편함을 저희에게 공유해주시면
                        <br />
                        더욱 건강한 서비스를 제공할 수 있도록 하겠습니다.
                    </p>
                    <div className="reason-list">
                        {WITHDRAW_REASONS.map((reason) => (
                            <label
                                key={reason.id}
                                className={`reason-item ${
                                    selectedReason === reason.id
                                        ? "selected"
                                        : ""
                                }`}
                                tabIndex={0}
                                role="radio"
                                aria-checked={selectedReason === reason.id}
                                aria-label={reason.label}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter" || e.key === " ") {
                                        e.preventDefault();
                                        handleReasonChange(reason.id);
                                    }
                                }}
                            >
                                <input
                                    type="radio"
                                    name="withdrawReason"
                                    value={reason.id}
                                    checked={selectedReason === reason.id}
                                    onChange={() =>
                                        handleReasonChange(reason.id)
                                    }
                                />
                                <span className="radio-mark"></span>
                                <span className="reason-text">
                                    {reason.label}
                                </span>
                            </label>
                        ))}
                    </div>
                </section>

                {/* 유의사항 섹션 */}
                <section className="withdraw-section">
                    <ul className="notice-list">
                        <h3>회원탈퇴 시 유의사항</h3>
                        <li>
                            <span className="notice-bullet">•</span>
                            <span>
                                구매한 수강권, 수강권 등의 유료 서비스 이용
                                권한이 즉시 소멸됩니다.
                            </span>
                        </li>
                        <li>
                            <span className="notice-bullet">•</span>
                            <span>
                                작성한 게시물, 리뷰, 댓글 등은 자동으로 삭제되지
                                않으며, 삭제를 원하시면 탈퇴 전 직접
                                삭제해주세요.
                            </span>
                        </li>
                        <li>
                            <span className="notice-bullet">•</span>
                            <span>
                                관련 법령에 따라 일부 정보는 일정 기간 보관 후
                                파기됩니다.
                                <br />
                                <span className="sub-notice">
                                    - 계약 또는 청약철회 등에 관한 기록: 5년
                                    <br />
                                    - 대금결제 및 재화 등의 공급에 관한 기록:
                                    5년
                                    <br />- 소비자의 불만 또는 분쟁처리에 관한
                                    기록: 3년
                                </span>
                            </span>
                        </li>

                        <li>
                            <span className="notice-bullet">•</span>
                            <span>
                                <strong>예약된 강습/연습반</strong> 스케쥴이
                                있거나
                                <strong> 주문/배송</strong>이 완료되지 않은
                                경우에는 탈퇴가 불가합니다.
                            </span>
                        </li>
                    </ul>
                </section>
                <div className="agree-wrap">
                    <label
                        className="checkbox-label"
                        tabIndex={0}
                        role="checkbox"
                        aria-checked={isAgreed}
                        aria-label="회원탈퇴 유의사항 동의"
                        onKeyDown={handleKeyDown}
                    >
                        <input
                            type="checkbox"
                            checked={isAgreed}
                            onChange={handleCheckboxChange}
                        />
                        <span className="checkmark"></span>
                        <span className="agree-text">
                            유의사항을 모두 확인하였으며, 회원탈퇴에 동의합니다.
                        </span>
                    </label>
                    <div className="btn-wrap">
                        <button
                            className={`btn btn-large ${
                                isAgreed ? "btn-purple500" : "btn-disabled"
                            }`}
                            onClick={handleWithdraw}
                            disabled={!isAgreed || isSubmitting}
                            aria-label="회원탈퇴 진행"
                        >
                            {isSubmitting ? "처리 중..." : "회원탈퇴"}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Withdraw;
